import { globalAuthContext, useGlobalAuth } from '@headbox-ui/auth-hook'
import React, { useEffect, FC, ReactNode, useMemo } from 'react'
import {
  AUTH_SERVICE,
  BUSINESS_GATEWAY,
  HEADBOX_ROOT_SERVICE,
  JWT_COOKIE_NAME,
  LOGIN_LINK,
  REFRESH_TOKEN_COOKIE_NAME,
} from '../../Constants'
import * as Sentry from '@sentry/react'
import { sendUserToProductFruits } from '../../Utilities/productFruits'
import { createClient } from 'urql'
import * as UrqlModule from 'urql'
import { createFromUrqlClient } from '@headbox-ui/auth-hook/dist/graphQLAdapter'
import { shouldRedirectToLoginPage } from './ShouldRedirectToLoginPage'

interface AuthenticatorProps {
  children: ReactNode
}

const AUTH_OPTIONS = {
  url: AUTH_SERVICE,
  exchanges: [UrqlModule.cacheExchange, UrqlModule.fetchExchange],
}

const authClient = createClient(AUTH_OPTIONS)

globalAuthContext.set({
  client: createFromUrqlClient({
    urqlModule: UrqlModule,
    client: authClient,
    url: AUTH_SERVICE,
  }),
  refreshTokenCookie: REFRESH_TOKEN_COOKIE_NAME,
  sessionTokenCookie: JWT_COOKIE_NAME,
  rootService: HEADBOX_ROOT_SERVICE,
})

const AuthenticatorInternal: FC<AuthenticatorProps> = ({ children }) => {
  const { authenticated, user, loading, jwt } = useGlobalAuth()
  const { pathname = '' } = window ? window.location : {}

  useEffect(() => {
    if (user) {
      Sentry.configureScope((scope) => {
        scope.setUser({
          id: String(user.id),
          email: user.email,
          roles: user.roles,
        })
      })
      sendUserToProductFruits(user)
    }
  }, [user])

  const appClient: UrqlModule.Client | null = useMemo(() => {
    return createClient({
      url: BUSINESS_GATEWAY,
      fetchOptions: jwt
        ? () => ({
          headers: { 'x-headbox-accesstoken': jwt },
        })
        : null,
      exchanges: [UrqlModule.cacheExchange, UrqlModule.fetchExchange],
    })
  }, [jwt])

  if (loading) {
    return null
  }

  if (!authenticated && shouldRedirectToLoginPage(pathname)) {
    window.location.assign(LOGIN_LINK)
    return null
  }

  return (
    <UrqlModule.Provider value={appClient}>{children}</UrqlModule.Provider>
  )
}

const Authenticator = ({
  children,
}: AuthenticatorProps): JSX.Element => {
  return (
    <UrqlModule.Provider value={authClient}>
      <AuthenticatorInternal>{children}</AuthenticatorInternal>
    </UrqlModule.Provider>
  )
}

export default Authenticator
